import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import Button from '~components/Button';
import Modal from '~components/Modal';
import ModalBase, { ModalBaseProps } from '~components/ModalBase';
import Spinner from '~components/Spinner';
import useBookmark from '~hooks/query/bookmark/useBookmark';
import useDeleteBookmarks from '~hooks/query/bookmark/useDeleteBookmarks';
import useGetMyInfoQuery from '~hooks/query/user/useMyInfoQuery';
import useMoveOneIdPage from '~hooks/useMoveOneIdPage';
import useHeaderStore from '~store/header';

import {
  BookmarkData,
  displayEmployee,
  displayNumberToKorean,
  displayPercentage,
  displaySalaryAvg,
  getBookmarks,
} from './helper';
import * as S from './style';

export default function ModalBookmarks({ isOpen, handleClose }: ModalBaseProps) {
  const router = useRouter();
  const { setIsModalGnbOpen } = useHeaderStore();
  const movieOneIdPage = useMoveOneIdPage();
  const { isActiveUser } = useGetMyInfoQuery();
  const { data: bookmarks, status } = useBookmark({
    isActiveUser,
    onError: (e) => {
      if (e.status === 401 || e.status === 406) {
        movieOneIdPage();
      }
    },
  });
  const [bookmarksList, setBookmarksList] = useState<BookmarkData[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>();
  const [selectedBookmarks, setSelectedBookmarks] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModaOpen] = useState(false);
  const { mutateAsync: deleteBookmarks } = useDeleteBookmarks();

  const handleClickRow = (companyHash: string) => {
    router.push(`/company/${companyHash}`);
    handleClose();
    setIsModalGnbOpen(false);
  };

  const handleCheckItem = (PK_NM_HASH: string) => {
    const newArray = [...selectedBookmarks];
    if (!selectedBookmarks.includes(PK_NM_HASH)) {
      newArray.push(PK_NM_HASH);
    } else {
      const index = selectedBookmarks.findIndex((value) => value === PK_NM_HASH);
      newArray.splice(index, 1);
    }
    setSelectedBookmarks(newArray);
  };

  const handleCheckAll = () => {
    if (bookmarksList.length === selectedBookmarks.length) {
      setSelectedBookmarks([]);
    } else {
      const newArray = bookmarksList.map((item) => item.PK_NM_HASH);
      setSelectedBookmarks(newArray);
    }
  };

  const handleDelete = () => {
    setIsModaOpen(true);
  };
  const handleConfirm = () => {
    deleteBookmarks(selectedBookmarks).then((repsonse) => {
      setSelectedBookmarks([]);
      setIsModaOpen(false);
    });
  };

  useEffect(() => {
    if (isOpen && status === 'success') {
      getBookmarks({ bookmarks: bookmarks?.map((bookmark) => bookmark.PK_NM_HASH) ?? [] }).then(
        (response) => {
          setBookmarksList(response.data.data);
          setIsLoading(false);
        },
      );
    }
  }, [bookmarks, status]);

  return (
    <ModalBase
      isOpen={isOpen}
      handleClose={handleClose}
      inlineCss={S.cssModalWrapper}
      hasCloseButton>
      {isLoading && status !== 'success' ? (
        <S.SpinnerWrapper>
          <Spinner width={48} height={48} />
        </S.SpinnerWrapper>
      ) : (
        <>
          <S.HeaderWrapper>
            <S.Title>스크랩 {bookmarksList.length}건</S.Title>
            <S.NoticeText>관심 기업을 스크랩하고 주요 정보를 비교해보세요.</S.NoticeText>
          </S.HeaderWrapper>
          <S.EditWrapper>
            <Button size='sm' onClick={() => setIsEditMode((prevState) => !prevState)}>
              {isEditMode ? '취소' : '편집'}
            </Button>
            {isEditMode && (
              <>
                <S.CountSelectedItem>
                  선택 <strong>{selectedBookmarks.length}</strong>건
                </S.CountSelectedItem>
                <Button
                  size='sm'
                  variant='magenta'
                  onClick={() => handleDelete()}
                  disabled={selectedBookmarks.length === 0}>
                  삭제
                </Button>
              </>
            )}
          </S.EditWrapper>
          <S.Contents>
            <S.TableWrapper>
              <colgroup>
                <col width={30} />
                <col width={40} />
                <col width={110} />
                <col width={92} />
                <col width={76} />
                <col width={55} />
                <col width={76} />
                <col width={76} />
                <col width={100} />
                <col width={100} />
                <col width={100} />
                <col width={55} />
              </colgroup>
              <S.TableRow>
                <S.TableColumnHeader>
                  <div style={{ display: isEditMode ? 'block' : 'none' }}>
                    <input
                      type='checkbox'
                      checked={bookmarksList.length === selectedBookmarks.length}
                      onChange={() => handleCheckAll()}
                    />
                  </div>
                </S.TableColumnHeader>
                <S.TableColumnHeader>순서</S.TableColumnHeader>
                <S.TableColumnHeader>기업명</S.TableColumnHeader>
                <S.TableColumnHeader>평균연봉</S.TableColumnHeader>
                <S.TableColumnHeader>인원</S.TableColumnHeader>
                <S.TableColumnHeader>업력</S.TableColumnHeader>
                <S.TableColumnHeader>입사율</S.TableColumnHeader>
                <S.TableColumnHeader>퇴사율</S.TableColumnHeader>
                <S.TableColumnHeader>인원증감률</S.TableColumnHeader>
                <S.TableColumnHeader>총 매출액</S.TableColumnHeader>
                <S.TableColumnHeader>1인당 매출액</S.TableColumnHeader>
                <S.TableColumnHeader>임금비율</S.TableColumnHeader>
              </S.TableRow>
              {bookmarksList.map((item, index) => (
                <S.TableRow key={index} onClick={() => handleClickRow(item.PK_NM_HASH)}>
                  <S.TableColumn>
                    <div style={{ display: isEditMode ? 'block' : 'none' }}>
                      <input
                        type='checkbox'
                        checked={selectedBookmarks.includes(item.PK_NM_HASH)}
                        onChange={() => handleCheckItem(item.PK_NM_HASH)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </S.TableColumn>
                  <S.TableColumn>{index + 1}</S.TableColumn>
                  <S.TableColumn>
                    <Link href={`/company/${item.PK_NM_HASH}`}>{item.CMPN_NM}</Link>
                  </S.TableColumn>
                  <S.TableColumn>{displaySalaryAvg(item.AVG_SALARY_FINAL)}</S.TableColumn>
                  <S.TableColumn>{displayEmployee(item.PRSN_BASE)}</S.TableColumn>
                  <S.TableColumn>
                    {Number(item.COMPANY_AGE.toFixed()) > 0
                      ? `${item.COMPANY_AGE.toFixed()}년`
                      : '1년 미만'}
                  </S.TableColumn>
                  <S.TableColumn>{displayPercentage(item.PRSN_IN_RATIO)}</S.TableColumn>
                  <S.TableColumn>{displayPercentage(item.PRSN_OUT_RATIO)}</S.TableColumn>
                  <S.TableColumn>{displayPercentage(item.PRSN_DIF_RATIO)}</S.TableColumn>
                  <S.TableColumn>{displayNumberToKorean(item.SALES_VALUE)}</S.TableColumn>
                  <S.TableColumn>{displayNumberToKorean(item.SALES_PER_MAN)}</S.TableColumn>
                  <S.TableColumn>{displayPercentage(item.SALARY_SALES_RATIO)}</S.TableColumn>
                </S.TableRow>
              ))}
            </S.TableWrapper>
          </S.Contents>
        </>
      )}
      <Modal
        isOpen={isModalOpen}
        title={`${selectedBookmarks.length}건 기업을 삭제하시겠습니까?`}
        confirmButtonText='삭제'
        confirmButtonVariant='dark'
        cancelButtonText='취소'
        handleConfirm={() => handleConfirm()}
        handleClose={() => setIsModaOpen(false)}
        handleCancel={() => setIsModaOpen(false)}
      />
    </ModalBase>
  );
}
